import { useCallback, useEffect, useRef } from "react";
import type { PastedAreaRow } from "./areas-paste";
import { isMultiCellPaste, parseClipboardRows } from "./areas-paste";

export interface AreasGridKeys {
  tbodyRef: React.RefObject<HTMLTableSectionElement | null>;
  handleCellKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleTablePaste: (e: React.ClipboardEvent<HTMLTableSectionElement>) => void;
}

function focusCellInRow(row: Element | null, col: number): void {
  if (!row) return;
  const cells = row.querySelectorAll<HTMLElement>('[data-cell="true"]');
  cells[col]?.focus();
}

function caretAtEdge(el: HTMLInputElement, edge: "start" | "end"): boolean {
  try {
    return edge === "start" ? el.selectionStart === 0 : el.selectionStart === el.value.length;
  } catch {
    return true;
  }
}

export function useAreasGridKeys(
  rowCount: number,
  onAdd: () => void,
  onPasteRows: (startIndex: number, rows: PastedAreaRow[]) => void,
): AreasGridKeys {
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const pendingFocusColRef = useRef<number | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reruns to refocus after a row is added
  useEffect(() => {
    const col = pendingFocusColRef.current;
    if (col == null) return;
    pendingFocusColRef.current = null;
    const rows = tbodyRef.current?.querySelectorAll<HTMLElement>("tr[data-area-row]");
    const lastRow = rows?.[rows.length - 1] ?? null;
    focusCellInRow(lastRow, col);
  }, [rowCount]);

  const handleCellKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const el = e.currentTarget;
      const tr = el.closest("tr");
      if (!tr) return;
      const cells = Array.from(tr.querySelectorAll<HTMLElement>('[data-cell="true"]'));
      const col = cells.indexOf(el);
      if (col === -1) return;

      if (e.key === "Enter") {
        e.preventDefault();
        const nextRow = tr.nextElementSibling;
        if (nextRow instanceof HTMLElement && nextRow.dataset.areaRow) {
          focusCellInRow(nextRow, col);
        } else {
          pendingFocusColRef.current = col;
          onAdd();
        }
        return;
      }
      if (e.key === "ArrowDown") {
        const nextRow = tr.nextElementSibling;
        if (nextRow instanceof HTMLElement && nextRow.dataset.areaRow) {
          e.preventDefault();
          focusCellInRow(nextRow, col);
        }
        return;
      }
      if (e.key === "ArrowUp") {
        const prevRow = tr.previousElementSibling;
        if (prevRow instanceof HTMLElement && prevRow.dataset.areaRow) {
          e.preventDefault();
          focusCellInRow(prevRow, col);
        }
        return;
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const atEdge = caretAtEdge(el, e.key === "ArrowLeft" ? "start" : "end");
        if (!atEdge) return;
        e.preventDefault();
        focusCellInRow(tr, e.key === "ArrowLeft" ? col - 1 : col + 1);
      }
    },
    [onAdd],
  );

  const handleTablePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTableSectionElement>) => {
      const target = e.target as HTMLElement;
      const tr = target.closest("tr[data-area-row]");
      if (!tr) return;
      const text = e.clipboardData.getData("text/plain");
      if (!isMultiCellPaste(text)) return;
      e.preventDefault();
      const index = Array.from(tbodyRef.current?.querySelectorAll("tr[data-area-row]") ?? []).indexOf(tr);
      if (index === -1) return;
      onPasteRows(index, parseClipboardRows(text));
    },
    [onPasteRows],
  );

  return { tbodyRef, handleCellKeyDown, handleTablePaste };
}
